import type { PbjPrediction, PbjSuggestedPlan } from "../../../utils/models";
import type { RecommendationType } from "./recommendations.constants";

// Pure helpers behind `useRecommendationSliders`: seeding the sliders from a plan and ordering a
// one-click commit. React-free so the hook stays about state and the rules here can be tested alone.

// DEFAULT: never taken — `visibleRecs` already drops levers with a null wage; this only narrows the type.
export const wageFor = (rec: RecommendationType, prediction: PbjPrediction): number => prediction.deptWages[rec.wageKey] ?? 0;

// The spend a plan puts on one lever.
// DEFAULT: a lever the plan has no step for is one it leaves alone — 0 is "no change", not a stand-in.
export const planSpendFor = (stepByAction: ReadonlyMap<string, { spend: number }>, recId: string): number => stepByAction.get(recId)?.spend ?? 0;

// Adds before cuts, each group in lever order. Suggested plans are additions-only, while manual
// slider positions may still contain both signs.
export const addsBeforeCuts = (recs: RecommendationType[], spendFor: (recId: string) => number): RecommendationType[] => [
	...recs.filter((r) => spendFor(r.id) >= 0),
	...recs.filter((r) => spendFor(r.id) < 0),
];

// Seed each visible rec's slider from a suggested plan: a rec mentioned by a step takes that step's
// spend; everything else sits at 0. Positions the sliders without touching the Action Plan.
export const spendMapFromPlan = (plan: PbjSuggestedPlan, visibleRecs: RecommendationType[]): Record<string, number> => {
	const stepByAction = new Map(plan.steps.map((s) => [s.actionId, s]));
	return Object.fromEntries(visibleRecs.map((r) => [r.id, planSpendFor(stepByAction, r.id)]));
};

// The visible recs a plan positions with a non-zero addition — seeds the "Suggested"
// highlight so a pre-positioned slider reads as suggested on load too.
export const suggestedIdsFromPlan = (plan: PbjSuggestedPlan, visibleRecs: RecommendationType[]): Set<string> => {
	const visibleIds = new Set<string>(visibleRecs.map((r) => r.id));
	return new Set(plan.steps.filter((s) => s.spend !== 0 && visibleIds.has(s.actionId)).map((s) => s.actionId));
};
